// src/components/inventory/ImportCSVModal.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • The "template" link works: a CSV template with the exact columns the server accepts, and
//     "current inventory in import format" (e.g. to edit and re-import elsewhere).
//   • Preview before uploading: the file is parsed in the browser (quotes, BOM), headers are
//     checked (name is required), rows with a missing name or an invalid number are listed —
//     the same rules the server applies; drag & drop; .csv / 5 MB check.
//   • The result is shown: "N imported, M skipped" with the row number and reason of every
//     skipped row (duplicate SKU …) instead of a generic "Import successful".
//   • FlowSense AI bar explains the import.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, VStack, useToast, useColorModeValue, Text, Link,
} from '@chakra-ui/react';
import { Box, HStack, Badge, Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon, Icon, List, ListItem } from '@chakra-ui/react'; // --- UPDATE
import { MdUploadFile, MdDownload } from 'react-icons/md'; // --- UPDATE
import inventoryService from '../../services/inventoryService';
// --- UPDATE (MOW+ v2)
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { parseCsv, downloadCsv } from '../../utils/inventoryCsv';

export const IMPORT_COLUMNS = Object.freeze(['sku', 'name', 'description', 'quantity', 'low_stock_threshold', 'reorder_quantity', 'unit_price', 'category_id', 'supplier_id', 'warehouse_location']);
const NUMERIC = ['quantity', 'low_stock_threshold', 'reorder_quantity', 'unit_price', 'category_id', 'supplier_id'];
const MAX_BYTES = 5 * 1024 * 1024;
export function analyseImport(text) {
  const rows = parseCsv(text);
  if (!rows.length) return { error: 'The file is empty.', headers: [], rows: [], problems: [], valid: 0 };
  const headers = rows[0].map((h) => String(h).trim().toLowerCase().replace(/\s+/g, '_'));
  if (!headers.includes('name')) return { error: 'The header row has no "name" column.', headers, rows: [], problems: [], valid: 0 };
  const unknown = headers.filter((h) => h && !IMPORT_COLUMNS.includes(h));
  const data = rows.slice(1).map((r) => Object.fromEntries(headers.map((h, i) => [h, r[i] !== undefined ? String(r[i]).trim() : ''])));
  const problems = [];
  data.forEach((r, i) => {
    const errs = [];
    if (!r.name) errs.push('name is missing');
    NUMERIC.forEach((k) => { if (r[k] !== undefined && r[k] !== '' && (!Number.isFinite(Number(r[k])) || Number(r[k]) < 0)) errs.push(`${k} "${r[k]}" is not a valid number`); });
    if (errs.length) problems.push({ row: i + 2, error: errs.join('; ') });
  });
  return { error: data.length ? null : 'The file has a header but no rows.', headers, unknown, rows: data, problems, valid: data.length - problems.length };
}
// --- END UPDATE

const ImportCSVModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // --- UPDATE
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const dropBorder = useColorModeValue('gray.300', 'whiteAlpha.300');
  const dropActive = useColorModeValue('teal.50', 'whiteAlpha.100');
  const headBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');
  // --- END UPDATE
  const toast = useToast();
  const modalBg = useColorModeValue("white", "navy.800");

  // --- UPDATE: read + analyse the file in the browser
  const takeFile = async (f) => {
    setResult(null);
    if (!f) { setFile(null); setPreview(null); return; }
    if (!/\.csv$/i.test(f.name) && f.type !== 'text/csv') { setFile(null); setPreview({ error: 'Choose a .csv file.' }); return; }
    if (f.size > MAX_BYTES) { setFile(null); setPreview({ error: 'The file is larger than 5 MB.' }); return; }
    setFile(f);
    try {
      const text = typeof f.text === 'function' ? await f.text() : await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || '')); r.onerror = reject; r.readAsText(f); });
      setPreview(analyseImport(text));
    } catch (_) { setPreview({ error: 'Could not read the file.' }); }
  };
  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const close = () => { reset(); onClose(); };
  const downloadTemplate = () => downloadCsv('flowsense-inventory-import-template.csv', IMPORT_COLUMNS,
    [['PVC-4', '4in PVC pipe', 'Schedule 40, 20 ft length', '40', '10', '50', '25.00', '', '', 'Depot A · Bay 1']]);
  const downloadCurrent = async () => {
    try {
      const r = await inventoryService.getAllItems();
      const items = Array.isArray(r) ? r : r?.data || [];
      downloadCsv('flowsense-inventory-current.csv', IMPORT_COLUMNS, items.map((i) => IMPORT_COLUMNS.map((k) => i[k] ?? '')));
    } catch (e) { toast({ title: 'Could not export', description: e.message, status: 'error' }); }
  };
  // --- END UPDATE

  const handleFileChange = (e) => {
    takeFile(e.target.files[0]); // --- UPDATE: was setFile(e.target.files[0])
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({ title: "No file selected.", status: 'warning', isClosable: true });
      return;
    }
    setIsSubmitting(true);

    try {
        const formData = new FormData();
        formData.append('file', file); // Append the raw file

        // The service needs to be updated to handle FormData
        const res = await inventoryService.importFromCSV(formData); // --- UPDATE: keep the result
        const body = res?.data && res.data.imported !== undefined ? res.data : res; // --- UPDATE

        // --- UPDATE: show what happened
        const skipped = Array.isArray(body?.skipped) ? body.skipped : [];
        setResult({ imported: body?.imported ?? null, skipped, message: body?.message || null });
        toast({ title: 'Import successful!', description: body?.message || 'Inventory has been updated.', status: skipped.length ? 'warning' : 'success', isClosable: true });
        onImportSuccess();
        if (!skipped.length) close();
        // --- END UPDATE
    } catch (error) {
        toast({ title: 'Import Failed', description: error.message || 'An unknown error occurred.', status: 'error', isClosable: true });
        setResult({ imported: 0, skipped: [], message: error.message || 'Import failed.' }); // --- UPDATE
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered size="3xl" scrollBehavior="inside">{/* --- UPDATE: wider for the preview */}
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Import Inventory from CSV</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* --- UPDATE */}
            <FlowSenseInsightBar screen="import" compact data={null} headline={preview && !preview.error ? `${preview.rows.length} rows read · ${preview.valid} ready · ${preview.problems.length} with problems` : 'Check the file here before it is uploaded.'}
              rulesAnswer={() => ['CSV import — how it works:', 'Columns: ' + IMPORT_COLUMNS.join(', ') + '.', 'Only "name" is required. Numbers must be 0 or more. A SKU that already exists is skipped (duplicate SKU).', preview?.problems?.length ? `This file: ${preview.problems.length} row(s) will be skipped — ${preview.problems.slice(0, 3).map((p) => `row ${p.row}: ${p.error}`).join('; ')}.` : null].filter(Boolean).join('\n')} />
            {/* --- END UPDATE */}
            <Text>
              Select a CSV file to import. The file must have headers:
              <Text as="b"> Name, Description, Quantity, Low Stock Threshold, Unit Price</Text>.
            </Text>
            <Text fontSize="sm">
              Note: Category and Supplier must be created manually first.
              You can download the current inventory as a <Link color="blue.400" onClick={downloadTemplate}>template</Link>.{/* --- UPDATE: wired */}
              {' '}Optional columns: sku, reorder_quantity, category_id, supplier_id, warehouse_location.{/* --- UPDATE */}
            </Text>
            {/* --- UPDATE */}
            <HStack spacing={2} flexWrap="wrap">
              <Button size="sm" variant="outline" leftIcon={<Icon as={MdDownload} />} onClick={downloadTemplate}>Download template</Button>
              <Button size="sm" variant="ghost" leftIcon={<Icon as={MdDownload} />} onClick={downloadCurrent}>Current inventory (import format)</Button>
            </HStack>
            <Box borderWidth="2px" borderStyle="dashed" borderColor={dragOver ? 'teal.400' : dropBorder} bg={dragOver ? dropActive : undefined} borderRadius="xl" p={4} textAlign="center"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer?.files?.[0]); }} data-testid="csv-drop">
              <Icon as={MdUploadFile} boxSize={8} color="teal.400" />
              <Text fontSize="sm" color={muted}>Drop a .csv here or choose a file</Text>
            {/* --- END UPDATE */}
            <FormControl isRequired>
              <FormLabel>CSV File</FormLabel>
              <Input type="file" accept=".csv" onChange={handleFileChange} p={1} aria-label="CSV file" />
            </FormControl>
            </Box>
            {/* --- UPDATE: preview */}
            {preview?.error && <Alert status="error" borderRadius="lg"><AlertIcon />{preview.error}</Alert>}
            {preview && !preview.error && (
              <Box>
                <HStack mb={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="blue">{preview.rows.length} rows</Badge>
                  <Badge colorScheme="green">{preview.valid} ready</Badge>
                  {preview.problems.length > 0 && <Badge colorScheme="orange">{preview.problems.length} will be skipped</Badge>}
                  {preview.unknown?.length > 0 && <Text fontSize="xs" color={muted}>Ignored columns: {preview.unknown.join(', ')}</Text>}
                </HStack>
                <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
                  <Table size="sm" aria-label="Import preview">
                    <Thead bg={headBg}><Tr><Th>Row</Th>{preview.headers.filter((h) => IMPORT_COLUMNS.includes(h)).map((h) => <Th key={h}>{h}</Th>)}</Tr></Thead>
                    <Tbody>{preview.rows.slice(0, 5).map((r, i) => (
                      <Tr key={i}><Td>{i + 2}</Td>{preview.headers.filter((h) => IMPORT_COLUMNS.includes(h)).map((h) => <Td key={h} fontSize="xs">{r[h]}</Td>)}</Tr>
                    ))}</Tbody>
                  </Table>
                </Box>
                {preview.rows.length > 5 && <Text fontSize="xs" color={muted} mt={1}>… and {preview.rows.length - 5} more rows</Text>}
                {preview.problems.length > 0 && (
                  <List mt={2} spacing={0.5} fontSize="xs" color="orange.500">{preview.problems.slice(0, 8).map((p) => <ListItem key={p.row}>Row {p.row}: {p.error}</ListItem>)}</List>
                )}
              </Box>
            )}
            {result && (
              <Alert status={result.skipped.length || !result.imported ? 'warning' : 'success'} borderRadius="lg" alignItems="start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="700">{result.message || `${result.imported ?? '—'} imported`}</Text>
                  {result.skipped.length > 0 && <List fontSize="xs" mt={1}>{result.skipped.slice(0, 10).map((s) => <ListItem key={`${s.row}-${s.error}`}>Row {s.row}: {s.error}</ListItem>)}</List>}
                </Box>
              </Alert>
            )}
            {/* --- END UPDATE */}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={close}>{result ? 'Close' : 'Cancel'}</Button>{/* --- UPDATE */}
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting} isDisabled={!file || !!preview?.error || (preview && !preview.valid)}>Upload and Import</Button>{/* --- UPDATE: disabled until the file is valid */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportCSVModal;
